import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography, TextField, Autocomplete } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserSecret } from '@fortawesome/pro-light-svg-icons';
import { setImpersonation } from '../../../store/impersonationSlice';
import { getData } from '../../../DataAccessLayer';
import { users, impersonation } from '../../../DataAccessLayer/services';
import { SIDEKICK_DASHBOARD_PATH } from '../../common/constants';
import MssModal from '../../common/MssModal';

function ImpersonationDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const courseId = useSelector((state) => state.course.courseId);
  const isImpersonating = useSelector((state) => state.impersonation.isImpersonating);

  const [text, setText] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (open) {
      setText('');
      setIsError(false);
      getUserOptions();
    }
  }, [open]);

  const getUserOptions = async () => {
    try {
      setIsLoading(true);
      const data = await getData(users);
      setUserOptions(data);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = async () => {
    try {
      const data = await getData(impersonation(text));
      dispatch(setImpersonation({ ...data, courseId }));
      // A resource loaded under the previous identity (a specific deck, quiz,
      // or editor) may not exist, or even belong to, the newly impersonated
      // user. Rather than let that page fail trying to load it, send the
      // admin back to the dashboard, which reloads fresh for the new user.
      if (pathname.startsWith(`${SIDEKICK_DASHBOARD_PATH}/`)) {
        navigate(SIDEKICK_DASHBOARD_PATH);
      }
      handleClose();
    } catch (err) {
      console.log(err);
      setIsError(true);
    }
  };

  const handleClose = () => {
    setText('');
    setIsError(false);
    onClose();
  };

  return (
    <MssModal
      isOpen={open}
      onClose={handleClose}
      isCloseable
      noHeader
      maxWidth="xs"
      id="impersonateModal"
      primaryButtonTitle={isImpersonating ? 'Switch' : 'Impersonate User'}
      onPrimaryClick={handleApply}
      isPrimaryButtonDisabled={!text}
    >
      <Stack direction="row" sx={{ mb: 2 }}>
        <FontAwesomeIcon icon={faUserSecret} fixedWidth size="lg" />
        <Typography component="h2">Impersonate User</Typography>
      </Stack>

      <Stack direction="row">
        <Typography component="label" sx={{ mt: 1, mr: 1 }}>
          User
        </Typography>
        <Box sx={{ width: '100%' }}>
          <Autocomplete
            key={open}
            freeSolo
            loading={isLoading}
            options={userOptions}
            defaultValue=""
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : `${option.name} (${option.midas})`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="MIDAS, Name or Email"
                variant="outlined"
                size="small"
                fullWidth
                error={isError}
                helperText={isError ? 'MIDAS not found' : ''}
              />
            )}
            filterOptions={(options, { inputValue }) => {
              const filtered = options.filter(
                (option) =>
                  option.midas.toLowerCase().includes(inputValue.toLowerCase()) ||
                  option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                  option.email.toLowerCase().includes(inputValue.toLowerCase()),
              );
              if (inputValue !== '' && !filtered.length) {
                filtered.push(inputValue);
              }
              return filtered;
            }}
            onInputChange={(_, newValue) => {
              setText(newValue);
              setIsError(false);
            }}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                setText(newValue);
              } else if (newValue) {
                setText(newValue.midas);
              } else {
                setText('');
              }
            }}
          />
        </Box>
      </Stack>
    </MssModal>
  );
}

export default ImpersonationDialog;
